import { useState, useEffect, useContext } from "react";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import {
    Card,
    Select,
    Row,
    Col,
    Table,
    Spin,
    Button,
} from "antd";

import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
} from "recharts";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

export default function LopChart() {
    const { backUrl, authData } = useContext(AuthContext);

    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [year, setYear] = useState(new Date().getFullYear());
    const [loading, setLoading] = useState(false);

    const [organisations, setOrganisations] = useState([]);
    const [organisation, setOrganisation] = useState(1);

    const [data, setData] = useState(null);
    const [chartType, setChartType] = useState("bar");
    const [deductionType, setDeductionType] = useState("total_deductions");

    const [showTable, setShowTable] = useState(false); // 🔥 NEW STATE

    const COLORS = ["#8884d8", "#82ca9d", "#ffc658", "#ff8042", "#8dd1e1", "#a4de6c", "#d0ed57"];

    // ===================== FETCH ORGANISATIONS =====================
    const fetchOrganisations = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/organisations/`, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });
            setOrganisations(res.data.results);
        } catch (err) {
            console.log("Error loading organisations:", err);
        }
    };

    useEffect(() => {
        fetchOrganisations();
    }, []);

    // ===================== FETCH API DATA =====================
    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await axios.get(
                `${backUrl}/api/reports/payroll-deductions/?month=${month}&year=${year}&organisation=${organisation}`,
                {
                    headers: {
                        Authorization: `Bearer ${authData?.access}`,
                    },
                }
            );
            setData(res.data);
        } catch (err) {
            console.log(err);
        }
        setLoading(false);
    };

    useEffect(() => {
        if (organisation !== "") fetchData();
    }, [month, year, organisation]);

    // ===================== DEPARTMENT WISE GROUPING =====================
    const departmentWise = () => {
        if (!data) return [];
        const groups = {};

        data.employees.forEach((emp) => {
            const dept = emp.department || "Unknown";

            if (!groups[dept]) {
                groups[dept] = 0;
            }
            groups[dept] += emp[deductionType] || 0;
        });

        return Object.keys(groups).map((dept) => ({
            department: dept,
            total: groups[dept],
        }));
    };

    // PIE CHART DATA
    const pieData = departmentWise().map((item) => ({
        name: item.department,
        value: item.total,
    }));

    // ===================== EXCEL DOWNLOAD =====================
    const downloadExcel = () => {
        const ws = XLSX.utils.json_to_sheet(data.employees);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Employees");

        const excelBuffer = XLSX.write(wb, {
            bookType: "xlsx",
            type: "array",
        });

        const file = new Blob([excelBuffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        saveAs(file, `deductions_${month}_${year}.xlsx`);
    };

    // ===================== EMPLOYEE TABLE COLUMNS =====================
    const columns = [
        { title: "Code", dataIndex: "employee_code" },
        { title: "Name", dataIndex: "employee_name" },
        { title: "Department", dataIndex: "department" },
        { title: "Designation", dataIndex: "designation" },
        { title: "PF", dataIndex: "pf" },
        { title: "ESI", dataIndex: "esi" },
        { title: "PT", dataIndex: "pt" },
        { title: "TDS", dataIndex: "tds" },
        { title: "LOP", dataIndex: "lop" },
        { title: "Advances", dataIndex: "advances" },
        { title: "Total", dataIndex: "total_deductions" },
    ];

    return (
        <div style={{ padding: "20px" }}>
            {/* ================= FILTERS ================= */}
            <Card style={{ marginBottom: "20px" }}>
                <Row gutter={20}>
                    {/* Organisation Filter */}
                    <Col span={6}>
                        <label>Organisation</label>
                        <Select
                            style={{ width: "100%" }}
                            value={organisation}
                            onChange={setOrganisation}
                            placeholder="Select organisation"
                            options={organisations.map((org) => ({
                                label: org.name,
                                value: org.id,
                            }))}
                        />
                    </Col>

                    <Col span={6}>
                        <label>Month</label>
                        <Select
                            style={{ width: "100%" }}
                            value={month}
                            onChange={setMonth}
                            options={Array.from({ length: 12 }, (_, i) => ({
                                label: i + 1,
                                value: i + 1,
                            }))}
                        />
                    </Col>

                    <Col span={6}>
                        <label>Year</label>
                        <Select
                            style={{ width: "100%" }}
                            value={year}
                            onChange={setYear}
                            options={[
                                { label: 2024, value: 2024 },
                                { label: 2025, value: 2025 },
                                { label: 2026, value: 2026 },
                            ]}
                        />
                    </Col>

                    <Col span={6}>
                        <label>Chart Type</label>
                        <Select
                            style={{ width: "100%" }}
                            value={chartType}
                            onChange={setChartType}
                            options={[
                                { label: "Bar Chart", value: "bar" },
                                { label: "Pie Chart", value: "pie" },
                            ]}
                        />
                    </Col>
                </Row>

                <Row gutter={20} style={{ marginTop: "15px" }}>
                    <Col span={6}>
                        <label>Deduction Type</label>
                        <Select
                            style={{ width: "100%" }}
                            value={deductionType}
                            onChange={setDeductionType}
                            options={[
                                { label: "Total", value: "total_deductions" },
                                { label: "PF", value: "pf" },
                                { label: "ESI", value: "esi" },
                                { label: "PT", value: "pt" },
                                { label: "TDS", value: "tds" },
                                { label: "LOP", value: "lop" },
                                { label: "Advances", value: "advances" },
                            ]}
                        />
                    </Col>
                </Row>
            </Card>

            {/* ================= ORG-WISE SUMMARY ================= */}
            {data && (
                <Card style={{ marginBottom: "20px" }} title="Organisation-wise Deductions Summary">
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart
                            data={[
                                {
                                    name: "Deductions",
                                    pf: data.summary.total_pf,
                                    esi: data.summary.total_esi,
                                    pt: data.summary.total_pt,
                                    tds: data.summary.total_tds,
                                    lop: data.summary.total_lop,
                                    advances: data.summary.total_advances,
                                    total: data.summary.total_deductions,
                                },
                            ]}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="pf" fill="#8884d8" />
                            <Bar dataKey="esi" fill="#82ca9d" />
                            <Bar dataKey="pt" fill="#ffc658" />
                            <Bar dataKey="tds" fill="#ff8042" />
                            <Bar dataKey="lop" fill="#8dd1e1" />
                            <Bar dataKey="advances" fill="#a4de6c" />
                            <Bar dataKey="total" fill="#d0ed57" />
                        </BarChart>
                    </ResponsiveContainer>
                </Card>
            )}

            {/* ================= DEPARTMENT WISE ================= */}
            {data && (
                <Card
                    style={{ marginBottom: "20px" }}
                    title={`Department-wise ${deductionType.toUpperCase()} Deductions`}
                >
                    <ResponsiveContainer width="100%" height={350}>
                        {chartType === "bar" ? (
                            <BarChart data={departmentWise()}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="department" />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                <Bar dataKey="total" fill="#82ca9d" />
                            </BarChart>
                        ) : (
                            <PieChart>
                                <Pie
                                    data={pieData}
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={130}
                                    dataKey="value"
                                    label
                                >
                                    {pieData.map((_, index) => (
                                        <Cell
                                            key={`cell-${index}`}
                                            fill={COLORS[index % COLORS.length]}
                                        />
                                    ))}
                                </Pie>
                                <Tooltip />
                            </PieChart>
                        )}
                    </ResponsiveContainer>
                </Card>
            )}

            {/* ================= EMPLOYEE LIST TABLE + EXCEL ================= */}
            {data && (
                <Card
                    title={
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span>Employee-wise Deduction Details</span>

                            <div style={{ display: "flex", gap: "10px" }}>
                                <Button onClick={() => setShowTable(!showTable)}>
                                    {showTable ? "Hide Table" : "Show Table"}
                                </Button>

                                <Button type="primary" onClick={downloadExcel}>
                                    Download Excel
                                </Button>
                            </div>
                        </div>
                    }
                >
                    {showTable && (
                        <Table
                            dataSource={data.employees}
                            columns={columns}
                            pagination={{ pageSize: 10 }}
                            rowKey="id"
                        />
                    )}
                </Card>
            )}
        </div>
    );
}
